import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CategoryIcon from '@mui/icons-material/Category';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PreviewIcon from '@mui/icons-material/Preview';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SaveIcon from '@mui/icons-material/Save';
import WelcomeModal from '../onboarding/WelcomeModal';
import {
  getOnboardingContent,
  OnboardingContentSection,
  OnboardingContentSettings,
  OnboardingFeatureItem,
  OnboardingHelpArticle,
  SupportContentAudience,
  updateOnboardingContent,
} from '../../services/onboardingService';

const createLocalId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const audienceOptions: Array<{ value: SupportContentAudience; label: string }> = [
  { value: 'all', label: 'Alla användare' },
  { value: 'editor', label: 'Redaktör + admin' },
  { value: 'admin', label: 'Endast admin' },
];

const audienceLabelMap: Record<SupportContentAudience, string> = {
  all: 'Alla',
  editor: 'Redaktör + admin',
  admin: 'Endast admin',
};

const getUniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>();
  const uniqueValues: string[] = [];

  values.forEach((value) => {
    const trimmed = value.trim();
    const normalized = trimmed.toLowerCase();
    if (!trimmed || seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    uniqueValues.push(trimmed);
  });

  return uniqueValues;
};

const ensureSettingsShape = (settings: OnboardingContentSettings): OnboardingContentSettings => {
  const articleCategories = settings.helpCenter.articles.map((article) => article.category);
  const categories = getUniqueStrings([
    ...(settings.helpCenter.categories || []),
    ...articleCategories,
  ]);

  return {
    ...settings,
    helpCenter: {
      ...settings.helpCenter,
      categories: categories.length > 0 ? categories : ['Övrigt'],
    },
  };
};

const moveItem = <T,>(items: T[], index: number, direction: -1 | 1): T[] => {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= items.length) {
    return items;
  }

  const nextItems = [...items];
  const [item] = nextItems.splice(index, 1);
  nextItems.splice(nextIndex, 0, item);
  return nextItems;
};

const createEmptyWelcomeSection = (): OnboardingContentSection => ({
  id: createLocalId('welcome-section'),
  text: '',
  audience: 'all',
});

const createEmptyFeature = (): OnboardingFeatureItem => ({
  id: createLocalId('welcome-feature'),
  label: '',
  audience: 'all',
});

const createEmptyArticle = (defaultCategory: string): OnboardingHelpArticle => ({
  id: createLocalId('help-article'),
  title: '',
  category: defaultCategory,
  audience: 'all',
  content: '',
});

const getArticleExcerpt = (content: string): string => {
  const firstLine = content.split('\n').find((line) => line.trim().length > 0);
  if (!firstLine) {
    return 'Ingen text ännu.';
  }
  return firstLine.length > 90 ? `${firstLine.slice(0, 90)}...` : firstLine;
};

const summaryCardSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#fbfcfe',
};

const SupportPageSettings: React.FC = () => {
  const [settings, setSettings] = useState<OnboardingContentSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAudience, setPreviewAudience] = useState<SupportContentAudience>('all');
  const [previewName, setPreviewName] = useState('Anna');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [popupExpanded, setPopupExpanded] = useState(false);
  const [helpExpanded, setHelpExpanded] = useState(true);
  const [articlesExpanded, setArticlesExpanded] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getOnboardingContent();
        setSettings(ensureSettingsShape(data));
      } catch (err: any) {
        console.error('Error fetching onboarding content:', err);
        setError(err.response?.data?.error || 'Kunde inte ladda supportsidan');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const updateSettings = (
    updater: (current: OnboardingContentSettings) => OnboardingContentSettings
  ) => {
    setSettings((current) => (current ? ensureSettingsShape(updater(current)) : current));
  };

  const handleSave = async () => {
    if (!settings) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const nextSettings = ensureSettingsShape(settings);
      const saved = await updateOnboardingContent(nextSettings);
      setSettings(ensureSettingsShape(saved));
      setSuccess('Supportsidan sparades.');
    } catch (err: any) {
      console.error('Error saving onboarding content:', err);
      setError(err.response?.data?.error || 'Kunde inte spara supportsidan');
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = () => {
    const nextCategory = newCategoryName.trim();
    if (!nextCategory) {
      return;
    }

    updateSettings((current) => {
      const categories = getUniqueStrings([
        ...current.helpCenter.categories,
        nextCategory,
      ]);

      return {
        ...current,
        helpCenter: {
          ...current.helpCenter,
          categories,
        },
      };
    });

    setNewCategoryName('');
    setArticlesExpanded(true);
  };

  const updateWelcomeSection = (
    index: number,
    updater: (item: OnboardingContentSection) => OnboardingContentSection
  ) => {
    updateSettings((current) => ({
      ...current,
      welcomeModal: {
        ...current.welcomeModal,
        sections: current.welcomeModal.sections.map((section, sectionIndex) =>
          sectionIndex === index ? updater(section) : section
        ),
      },
    }));
  };

  const updateFeature = (
    index: number,
    updater: (item: OnboardingFeatureItem) => OnboardingFeatureItem
  ) => {
    updateSettings((current) => ({
      ...current,
      welcomeModal: {
        ...current.welcomeModal,
        features: current.welcomeModal.features.map((feature, featureIndex) =>
          featureIndex === index ? updater(feature) : feature
        ),
      },
    }));
  };

  const updateArticle = (
    index: number,
    updater: (item: OnboardingHelpArticle) => OnboardingHelpArticle
  ) => {
    updateSettings((current) => ({
      ...current,
      helpCenter: {
        ...current.helpCenter,
        articles: current.helpCenter.articles.map((article, articleIndex) =>
          articleIndex === index ? updater(article) : article
        ),
      },
    }));
  };

  const availableCategories = useMemo(() => {
    if (!settings) return [];
    return getUniqueStrings([
      ...settings.helpCenter.categories,
      ...settings.helpCenter.articles.map((article) => article.category),
    ]);
  }, [settings]);

  const popupSummary = useMemo(() => {
    if (!settings) return [];
    return [
      `${settings.welcomeModal.sections.length} avsnitt`,
      `${settings.welcomeModal.features.length} punkter`,
    ];
  }, [settings]);

  const helpSummary = useMemo(() => {
    if (!settings) return [];
    return [
      `${availableCategories.length} kategorier`,
      `${settings.helpCenter.articles.length} artiklar`,
    ];
  }, [availableCategories.length, settings]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="240px">
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return <Alert severity="error">Kunde inte ladda supportsidan.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1240 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3 },
          mb: 3,
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(180deg, #f9fbff 0%, #eef4fb 100%)',
        }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#072f53', fontWeight: 700 }}>
              Supportsida
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Redigera onboardingens hjälpsida och startpopup. Innehåll kan riktas till alla,
              redaktör + admin eller endast admin.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`${settings.welcomeModal.sections.length} popup-avsnitt`} color="primary" variant="outlined" />
            <Chip label={`${settings.welcomeModal.features.length} popup-punkter`} color="primary" variant="outlined" />
            <Chip label={`${settings.helpCenter.articles.length} hjälpartiklar`} color="primary" variant="outlined" />
            <Chip label={`${availableCategories.length} kategorier`} color="primary" variant="outlined" />
          </Stack>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', lg: 'flex-end' }}
          >
            <TextField
              select
              label="Förhandsvisa som"
              size="small"
              value={previewAudience}
              onChange={(event) => setPreviewAudience(event.target.value as SupportContentAudience)}
              sx={{ minWidth: { xs: '100%', sm: 220 } }}
            >
              {audienceOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Visningsnamn i preview"
              size="small"
              value={previewName}
              onChange={(event) => setPreviewName(event.target.value)}
              sx={{ minWidth: { xs: '100%', sm: 220 } }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<PreviewIcon />}
                onClick={() => setPreviewOpen(true)}
              >
                Se preview
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress color="inherit" size={18} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                Spara supportsida
              </Button>
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Förhandsvisningen använder dina nuvarande ändringar direkt, även innan du sparar.
          </Typography>
        </Stack>
      </Paper>

      <Stack spacing={2.5}>
        <Accordion
          expanded={popupExpanded}
          onChange={(_event, expanded) => setPopupExpanded(expanded)}
          disableGutters
          sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1.25 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1.5}
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <RocketLaunchIcon sx={{ color: '#072f53' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#072f53', fontWeight: 700 }}>
                    Startpopup
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Startar nu infälld. Öppna när du vill justera popupens innehåll och CTA.
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {popupSummary.map((label) => (
                  <Chip key={label} label={label} size="small" />
                ))}
              </Stack>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 3, pt: 1 }}>
            <Stack spacing={2.5}>
              <Paper variant="outlined" sx={{ ...summaryCardSx, p: 2.5 }}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#072f53' }}>
                    Grundinställningar
                  </Typography>

                  <TextField
                    label="Rubrik"
                    value={settings.welcomeModal.title}
                    onChange={(event) =>
                      updateSettings((current) => ({
                        ...current,
                        welcomeModal: {
                          ...current.welcomeModal,
                          title: event.target.value,
                        },
                      }))
                    }
                    fullWidth
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.welcomeModal.appendUserName}
                        onChange={(event) =>
                          updateSettings((current) => ({
                            ...current,
                            welcomeModal: {
                              ...current.welcomeModal,
                              appendUserName: event.target.checked,
                            },
                          }))
                        }
                      />
                    }
                    label="Lägg till användarens namn i rubriken"
                  />

                  <TextField
                    label="Underrubrik"
                    value={settings.welcomeModal.subtitle}
                    onChange={(event) =>
                      updateSettings((current) => ({
                        ...current,
                        welcomeModal: {
                          ...current.welcomeModal,
                          subtitle: event.target.value,
                        },
                      }))
                    }
                    fullWidth
                  />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <TextField
                      label="Primär knapp"
                      value={settings.welcomeModal.primaryButtonLabel}
                      onChange={(event) =>
                        updateSettings((current) => ({
                          ...current,
                          welcomeModal: {
                            ...current.welcomeModal,
                            primaryButtonLabel: event.target.value,
                          },
                        }))
                      }
                      fullWidth
                    />

                    <TextField
                      label="Sekundär knapp"
                      value={settings.welcomeModal.secondaryButtonLabel}
                      onChange={(event) =>
                        updateSettings((current) => ({
                          ...current,
                          welcomeModal: {
                            ...current.welcomeModal,
                            secondaryButtonLabel: event.target.value,
                          },
                        }))
                      }
                      fullWidth
                    />
                  </Box>

                  <TextField
                    label="Fotnot"
                    value={settings.welcomeModal.footerNote}
                    onChange={(event) =>
                      updateSettings((current) => ({
                        ...current,
                        welcomeModal: {
                          ...current.welcomeModal,
                          footerNote: event.target.value,
                        },
                      }))
                    }
                    fullWidth
                    multiline
                    minRows={2}
                  />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ ...summaryCardSx, p: 2.5 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#072f53', mb: 0.5 }}>
                      Popup-avsnitt
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Lägg till ett eller flera textavsnitt. Dessa kan riktas till olika roller.
                    </Typography>
                  </Box>

                  <Stack spacing={1.5}>
                    {settings.welcomeModal.sections.map((section, index) => (
                      <Card key={section.id} variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip
                                label={audienceLabelMap[section.audience]}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />

                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                  onClick={() =>
                                    updateSettings((current) => ({
                                      ...current,
                                      welcomeModal: {
                                        ...current.welcomeModal,
                                        sections: moveItem(current.welcomeModal.sections, index, -1),
                                      },
                                    }))
                                  }
                                  disabled={index === 0}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() =>
                                    updateSettings((current) => ({
                                      ...current,
                                      welcomeModal: {
                                        ...current.welcomeModal,
                                        sections: moveItem(current.welcomeModal.sections, index, 1),
                                      },
                                    }))
                                  }
                                  disabled={index === settings.welcomeModal.sections.length - 1}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    updateSettings((current) => ({
                                      ...current,
                                      welcomeModal: {
                                        ...current.welcomeModal,
                                        sections: current.welcomeModal.sections.filter(
                                          (_item, itemIndex) => itemIndex !== index
                                        ),
                                      },
                                    }))
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            <TextField
                              select
                              label="Målgrupp"
                              value={section.audience}
                              onChange={(event) =>
                                updateWelcomeSection(index, (current) => ({
                                  ...current,
                                  audience: event.target.value as SupportContentAudience,
                                }))
                              }
                              size="small"
                              sx={{ maxWidth: 260 }}
                            >
                              {audienceOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </TextField>

                            <TextField
                              label="Text"
                              value={section.text}
                              onChange={(event) =>
                                updateWelcomeSection(index, (current) => ({
                                  ...current,
                                  text: event.target.value,
                                }))
                              }
                              fullWidth
                              multiline
                              minRows={3}
                            />
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      updateSettings((current) => ({
                        ...current,
                        welcomeModal: {
                          ...current.welcomeModal,
                          sections: [...current.welcomeModal.sections, createEmptyWelcomeSection()],
                        },
                      }))
                    }
                  >
                    Lägg till popup-avsnitt
                  </Button>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ ...summaryCardSx, p: 2.5 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#072f53', mb: 0.5 }}>
                      Popup-punkter
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Korta stödord som visas visuellt i popupen.
                    </Typography>
                  </Box>

                  <Stack spacing={1.5}>
                    {settings.welcomeModal.features.map((feature, index) => (
                      <Card key={feature.id} variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Stack spacing={2}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip
                                label={audienceLabelMap[feature.audience]}
                                color="primary"
                                variant="outlined"
                                size="small"
                              />

                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                  onClick={() =>
                                    updateSettings((current) => ({
                                      ...current,
                                      welcomeModal: {
                                        ...current.welcomeModal,
                                        features: moveItem(current.welcomeModal.features, index, -1),
                                      },
                                    }))
                                  }
                                  disabled={index === 0}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() =>
                                    updateSettings((current) => ({
                                      ...current,
                                      welcomeModal: {
                                        ...current.welcomeModal,
                                        features: moveItem(current.welcomeModal.features, index, 1),
                                      },
                                    }))
                                  }
                                  disabled={index === settings.welcomeModal.features.length - 1}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  color="error"
                                  onClick={() =>
                                    updateSettings((current) => ({
                                      ...current,
                                      welcomeModal: {
                                        ...current.welcomeModal,
                                        features: current.welcomeModal.features.filter(
                                          (_item, itemIndex) => itemIndex !== index
                                        ),
                                      },
                                    }))
                                  }
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 260px' },
                                gap: 2,
                              }}
                            >
                              <TextField
                                label="Punkt"
                                value={feature.label}
                                onChange={(event) =>
                                  updateFeature(index, (current) => ({
                                    ...current,
                                    label: event.target.value,
                                  }))
                                }
                                fullWidth
                              />

                              <TextField
                                select
                                label="Målgrupp"
                                value={feature.audience}
                                onChange={(event) =>
                                  updateFeature(index, (current) => ({
                                    ...current,
                                    audience: event.target.value as SupportContentAudience,
                                  }))
                                }
                                fullWidth
                              >
                                {audienceOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>

                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() =>
                      updateSettings((current) => ({
                        ...current,
                        welcomeModal: {
                          ...current.welcomeModal,
                          features: [...current.welcomeModal.features, createEmptyFeature()],
                        },
                      }))
                    }
                  >
                    Lägg till popup-punkt
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={helpExpanded}
          onChange={(_event, expanded) => setHelpExpanded(expanded)}
          disableGutters
          sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1.25 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1.5}
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <HelpOutlineIcon sx={{ color: '#072f53' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#072f53', fontWeight: 700 }}>
                    Hjälpsida
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gemensamma texter för hjälplådan och dess struktur.
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {helpSummary.map((label) => (
                  <Chip key={label} label={label} size="small" />
                ))}
              </Stack>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 3, pt: 1 }}>
            <Paper variant="outlined" sx={{ ...summaryCardSx, p: 2.5 }}>
              <Stack spacing={2.5}>
                <TextField
                  label="Titel i hjälplådan"
                  value={settings.helpCenter.drawerTitle}
                  onChange={(event) =>
                    updateSettings((current) => ({
                      ...current,
                      helpCenter: {
                        ...current.helpCenter,
                        drawerTitle: event.target.value,
                      },
                    }))
                  }
                  fullWidth
                />

                <TextField
                  label="Placeholder i sökfält"
                  value={settings.helpCenter.searchPlaceholder}
                  onChange={(event) =>
                    updateSettings((current) => ({
                      ...current,
                      helpCenter: {
                        ...current.helpCenter,
                        searchPlaceholder: event.target.value,
                      },
                    }))
                  }
                  fullWidth
                />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Rubrik för snabbåtgärder"
                    value={settings.helpCenter.quickActionsTitle}
                    onChange={(event) =>
                      updateSettings((current) => ({
                        ...current,
                        helpCenter: {
                          ...current.helpCenter,
                          quickActionsTitle: event.target.value,
                        },
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    label="Knapptext för att starta guiden igen"
                    value={settings.helpCenter.restartTourLabel}
                    onChange={(event) =>
                      updateSettings((current) => ({
                        ...current,
                        helpCenter: {
                          ...current.helpCenter,
                          restartTourLabel: event.target.value,
                        },
                      }))
                    }
                    fullWidth
                  />
                </Box>

                <TextField
                  label="Rubrik för hjälpartiklar"
                  value={settings.helpCenter.articlesHeading}
                  onChange={(event) =>
                    updateSettings((current) => ({
                      ...current,
                      helpCenter: {
                        ...current.helpCenter,
                        articlesHeading: event.target.value,
                      },
                    }))
                  }
                  fullWidth
                />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <TextField
                    label="Tom sökning - rubrik"
                    value={settings.helpCenter.emptySearchTitle}
                    onChange={(event) =>
                      updateSettings((current) => ({
                        ...current,
                        helpCenter: {
                          ...current.helpCenter,
                          emptySearchTitle: event.target.value,
                        },
                      }))
                    }
                    fullWidth
                  />

                  <TextField
                    label="Tom sökning - beskrivning"
                    value={settings.helpCenter.emptySearchDescription}
                    onChange={(event) =>
                      updateSettings((current) => ({
                        ...current,
                        helpCenter: {
                          ...current.helpCenter,
                          emptySearchDescription: event.target.value,
                        },
                      }))
                    }
                    fullWidth
                  />
                </Box>

                <TextField
                  label="Footer-text"
                  value={settings.helpCenter.footerText}
                  onChange={(event) =>
                    updateSettings((current) => ({
                      ...current,
                      helpCenter: {
                        ...current.helpCenter,
                        footerText: event.target.value,
                      },
                    }))
                  }
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Stack>
            </Paper>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={articlesExpanded}
          onChange={(_event, expanded) => setArticlesExpanded(expanded)}
          disableGutters
          sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 3, py: 1.25 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={1.5}
              width="100%"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CategoryIcon sx={{ color: '#072f53' }} />
                <Box>
                  <Typography variant="h6" sx={{ color: '#072f53', fontWeight: 700 }}>
                    Hjälpartiklar
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Hantera artiklar, kategorier och målgrupper med dropdown i stället för fri text.
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`${availableCategories.length} kategorier`} size="small" />
                <Chip label={`${settings.helpCenter.articles.length} artiklar`} size="small" />
              </Stack>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 3, pt: 1 }}>
            <Stack spacing={2.5}>
              <Paper variant="outlined" sx={{ ...summaryCardSx, p: 2.5 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#072f53', mb: 0.5 }}>
                      Kategorier
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Skapa kategorier här och välj sedan kategori från dropdown på varje artikel.
                    </Typography>
                  </Box>

                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                  >
                    <TextField
                      label="Ny kategori"
                      size="small"
                      value={newCategoryName}
                      onChange={(event) => setNewCategoryName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleAddCategory();
                        }
                      }}
                      sx={{ minWidth: { xs: '100%', md: 280 } }}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddCategory}
                    >
                      Lägg till kategori
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {availableCategories.map((category) => (
                      <Chip
                        key={category}
                        label={`${category} (${settings.helpCenter.articles.filter((article) => article.category === category).length})`}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Stack>
              </Paper>

              <Stack spacing={2}>
                {settings.helpCenter.articles.map((article, index) => (
                  <Card key={article.id} variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            gap: 1.5,
                            flexDirection: { xs: 'column', md: 'row' },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#072f53' }}>
                              {article.title?.trim() || `Artikel ${index + 1}`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getArticleExcerpt(article.content)}
                            </Typography>
                          </Box>

                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip label={article.category} size="small" />
                            <Chip label={audienceLabelMap[article.audience]} size="small" variant="outlined" />
                            <IconButton
                              onClick={() =>
                                updateSettings((current) => ({
                                  ...current,
                                  helpCenter: {
                                    ...current.helpCenter,
                                    articles: moveItem(current.helpCenter.articles, index, -1),
                                  },
                                }))
                              }
                              disabled={index === 0}
                            >
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() =>
                                updateSettings((current) => ({
                                  ...current,
                                  helpCenter: {
                                    ...current.helpCenter,
                                    articles: moveItem(current.helpCenter.articles, index, 1),
                                  },
                                }))
                              }
                              disabled={index === settings.helpCenter.articles.length - 1}
                            >
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() =>
                                updateSettings((current) => ({
                                  ...current,
                                  helpCenter: {
                                    ...current.helpCenter,
                                    articles: current.helpCenter.articles.filter(
                                      (_item, itemIndex) => itemIndex !== index
                                    ),
                                  },
                                }))
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Box>

                        <TextField
                          label="Rubrik"
                          value={article.title}
                          onChange={(event) =>
                            updateArticle(index, (current) => ({
                              ...current,
                              title: event.target.value,
                            }))
                          }
                          fullWidth
                        />

                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 260px' },
                            gap: 2,
                          }}
                        >
                          <TextField
                            select
                            label="Kategori"
                            value={article.category}
                            onChange={(event) =>
                              updateArticle(index, (current) => ({
                                ...current,
                                category: event.target.value,
                              }))
                            }
                            fullWidth
                          >
                            {availableCategories.map((category) => (
                              <MenuItem key={category} value={category}>
                                {category}
                              </MenuItem>
                            ))}
                          </TextField>

                          <TextField
                            select
                            label="Målgrupp"
                            value={article.audience}
                            onChange={(event) =>
                              updateArticle(index, (current) => ({
                                ...current,
                                audience: event.target.value as SupportContentAudience,
                              }))
                            }
                            fullWidth
                          >
                            {audienceOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>

                        <TextField
                          label="Innehåll"
                          value={article.content}
                          onChange={(event) =>
                            updateArticle(index, (current) => ({
                              ...current,
                              content: event.target.value,
                            }))
                          }
                          fullWidth
                          multiline
                          minRows={6}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() =>
                  updateSettings((current) => ({
                    ...current,
                    helpCenter: {
                      ...current.helpCenter,
                      articles: [
                        ...current.helpCenter.articles,
                        createEmptyArticle(availableCategories[0] || 'Övrigt'),
                      ],
                    },
                  }))
                }
              >
                Lägg till hjälpartikel
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>

      <WelcomeModal
        open={previewOpen}
        onStartTour={() => setPreviewOpen(false)}
        onSkip={() => setPreviewOpen(false)}
        userName={previewName.trim() || undefined}
        viewerAudience={previewAudience}
        content={settings.welcomeModal}
      />
    </Box>
  );
};

export default SupportPageSettings;
